import React, { useEffect } from 'react';
import { StepProps, FormData, AddressDetail, UboDetail, AttachmentItem, TaxablePersonDetails, JuridicalPersonSpecifics, NaturalPersonSpecifics, TaxGroupSpecifics, UnincorporatedPartnershipSpecifics, Elections, AccountingSchedule, AccountingAdjustments, ReliefApplications, OtherAdjustments, TaxLiability, FreeZoneScheduleData, BusinessActivityItem, RevenuePercentageItem, UnincorporatedPartnershipItem, TaxGroupMemberItem } from '../../types';
import { Checkbox } from '../ui/Checkbox';
import { Input } from '../ui/Input';
import { translations } from '../../i18n/translations';
import { calculateTaxableIncomeBeforeLossUtilisation, calculateTaxableIncomeAfterTaxLoss, calculateCTLiabilityBeforeCredits, calculateCorporateTaxPayable } from '../../utils/calculations';

// --- HELPERS ---

const isValueEmpty = (value: any): boolean => {
  if (value === null || value === undefined || value === '' || value === false || value === '0.00' || (typeof value === 'number' && value === 0)) return true;
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    return value.every(item => typeof item === 'object' && item !== null && Object.values(item).every(v => isValueEmpty(v) || Object.keys(item).length === 1 && item.hasOwnProperty('id')));
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value instanceof File) return false;
    if (Object.keys(value).length === 0) return true;
    return Object.entries(value).every(([k, v]) => k === 'id' || isValueEmpty(v));
  }
  return false;
};


const hasData = (obj: any): boolean => {
    if (obj === null || obj === undefined) return false;
    if (typeof obj !== 'object') return !isValueEmpty(obj);
    if (Array.isArray(obj)) return obj.some(item => hasData(item));
    return Object.entries(obj).some(([key, value]) => {
      // Exclude calculation helper fields from hasData check
      if (key.startsWith('total') && key.endsWith('Calculated')) return false;
      if (key === 'id') return false;
      return !isValueEmpty(value);
    });
};


const renderValue = (value: any, t: StepProps['t'], key?: string): React.ReactNode => {
    if (isValueEmpty(value)) return '-';
    if (typeof value === 'boolean') return value ? t('yes') : t('no');
    if(key && (key.includes('emirate') || key.includes('Emirate'))) return t(`emirate_${String(value).toLowerCase()}` as any) || value;
    if(key && (key.includes('city') || key.includes('City'))) return t(`city_${String(value).toLowerCase()}_c` as any) || value;
    if(key && (key.includes('country') || key.includes('Country'))) return t(`country_${String(value).toLowerCase()}` as any) || value;
    if(key === 'filingAs') return t(`filing_as_${String(value)}` as any) || value;
    if(key === 'accountingBasis') return t(String(value) as 'cash' | 'accrual') || value;
    if(key === 'nonResidentFilingBasis' && value) return t(value as any) || value;
    if(typeof value === 'number') return value.toFixed(2);
    return String(value);
};

// --- DISPLAY COMPONENTS ---

const DataDisplayRow: React.FC<{ label: string; value: React.ReactNode; isCalculated?: boolean }> = ({ label, value, isCalculated }) => {
  if (value === null || value === undefined || value === '' || value === '-') return null;
  return (
    <>
      <dt>{label}</dt>
      <dd className={isCalculated ? 'value-calculated' : ''}>{value}</dd>
    </>
  );
};

const SectionWrapper: React.FC<{ title: string; children: React.ReactNode; condition?: boolean }> = ({ title, children, condition = true }) => {
    if (!condition) return null;
    return (
        <div className="section-wrapper mb-6">
            <h3 className="section-title">{title}</h3>
            {children}
        </div>
    );
};

const SubSectionWrapper: React.FC<{ title: string; children: React.ReactNode; condition?: boolean }> = ({ title, children, condition = true }) => {
    if (!condition) return null;
    return (
        <div className="subsection-wrapper mt-4">
            <h4 className="subsection-title">{title}</h4>
            <dl>{children}</dl>
        </div>
    );
};


const ListDisplay: React.FC<{ label: string; items?: any[]; renderItem: (item: any, index: number) => React.ReactNode; }> = ({ label, items, renderItem }) => {
  if (!items || items.length === 0 || !items.some(hasData)) return null;

  return (
    <div className="break-inside-avoid">
        <h4 className="subsection-title">{label}</h4>
        {items.filter(hasData).map((item, index) => (
            <div key={item.id || index} className="p-2 my-2 bg-gray-50 rounded border-l-2 border-gray-300 break-inside-avoid">
            {renderItem(item, index)}
            </div>
        ))}
    </div>
  );
};


const AddressDisplay: React.FC<{ label: string; address?: AddressDetail; t: StepProps['t'] }> = ({ label, address, t }) => {
  if (!address || !hasData(address)) return null;
  return (
    <SubSectionWrapper title={label}>
      <DataDisplayRow label={t('address_line1_label')} value={renderValue(address.addressLine1, t)} />
      <DataDisplayRow label={t('address_line2_label')} value={renderValue(address.addressLine2, t)} />
      <DataDisplayRow label={t('city_label')} value={renderValue(address.city, t, 'city')} />
      <DataDisplayRow label={t('emirate_state_label')} value={renderValue(address.emirateOrState, t, 'emirate')} />
      <DataDisplayRow label={t('po_box_label')} value={renderValue(address.poBox, t)} />
      <DataDisplayRow label={t('country_label')} value={renderValue(address.country, t, 'country')} />
    </SubSectionWrapper>
  );
};

// --- MAIN COMPONENT ---

export const ReviewAndDeclaration: React.FC<StepProps> = ({ formData, onChange, errors, t }) => {
  const { review: reviewData, taxablePersonDetails: tpData, elections, accounting, accountingAdjustments, relief, otherAdjustments, taxLiability, freeZoneSchedule } = formData;
  
  const calculatedTaxableIncomeBeforeLoss = calculateTaxableIncomeBeforeLossUtilisation(formData);
  const calculatedTaxableIncomeAfterLoss = calculateTaxableIncomeAfterTaxLoss(calculatedTaxableIncomeBeforeLoss, taxLiability.taxLossesUtilisedCurrentPeriod);
  const calculatedCtLiabilityBeforeCredits = calculateCTLiabilityBeforeCredits(calculatedTaxableIncomeAfterLoss, elections, tpData, freeZoneSchedule);
  const calculatedPayable = calculateCorporateTaxPayable(calculatedCtLiabilityBeforeCredits, taxLiability.taxCreditsApplied);

  const isFzpSubjectTo0Rate = (
    (tpData.filingAs === 'free_zone_person' || tpData.juridicalPersonSpecifics?.isFreeZonePerson === 'yes') &&
    tpData.juridicalPersonSpecifics?.electionToForego0RateFZ === 'no' &&
    tpData.juridicalPersonSpecifics?.meetsQualifyingFZRequirements === 'yes'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-trn', tpData.trn || 'Not Provided');
  }, [tpData.trn]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-[#1a2c46] mb-6 no-print">{t('step8_title')}</h2>
      
      <div id="printArea">
        {elections.smallBusinessRelief && (
          <div className="p-4 mb-6 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 section-wrapper">
            <h3 className="font-bold text-lg">{t('sbr_active_title')}</h3>
            <p>{t('sbr_active_message')}</p>
          </div>
        )}

        <SectionWrapper title={t('step1_title')}>
          <dl>
              <DataDisplayRow label={t('filing_as_label')} value={renderValue(tpData.filingAs, t, 'filingAs')} />
              <DataDisplayRow label={t('taxable_person_name_label')} value={tpData.taxablePersonName} />
              <DataDisplayRow label={t('trn_label')} value={tpData.trn} />
              <DataDisplayRow label={t('primary_business_activity_label')} value={tpData.primaryBusinessActivity} />
              <DataDisplayRow label={t('tax_period_start_date_label')} value={tpData.taxPeriodStartDate} />
              <DataDisplayRow label={t('tax_period_end_date_label')} value={tpData.taxPeriodEndDate} />
          </dl>
          <AddressDisplay label={t('main_business_address_title')} address={tpData.mainAddress} t={t} />
          <SubSectionWrapper title={t('contact_person_details_title')}>
              <DataDisplayRow label={t('contact_name_label')} value={tpData.contactPerson.contactName} />
              <DataDisplayRow label={t('contact_email_label')} value={tpData.contactPerson.contactEmail} />
              <DataDisplayRow label={t('contact_mobile_label')} value={tpData.contactPerson.contactMobile} />
              <DataDisplayRow label={t('contact_landline_label')} value={renderValue(tpData.contactPerson.contactLandline, t)} />
          </SubSectionWrapper>
          {tpData.isUboInfoDisclosed === 'yes' && <ListDisplay label={t('ubo_information_title')} items={tpData.uboDetails} renderItem={(ubo: UboDetail, i) => (
              <dl>
                  <DataDisplayRow label={t('ubo_name_label')} value={ubo.uboName}/>
                  <DataDisplayRow label={t('ubo_trn_tax_id_label')} value={ubo.uboTrnTaxId}/>
                  <DataDisplayRow label={t('ubo_nationality_label')} value={renderValue(ubo.uboNationality, t, 'country')}/>
                  <DataDisplayRow label={t('ubo_country_of_residence_label')} value={renderValue(ubo.uboCountryOfResidence, t, 'country')}/>
              </dl>
          )} />}
        </SectionWrapper>
        
        <SectionWrapper title={t('juridical_person_specifics_title')} condition={hasData(tpData.juridicalPersonSpecifics) && (tpData.filingAs === 'juridical_person' || tpData.filingAs === 'free_zone_person')}>
          <dl>
              <DataDisplayRow label={t('juridical_person_incorporation_date_label')} value={renderValue(tpData.juridicalPersonSpecifics.incorporationDate, t)} />
              <DataDisplayRow label={t('is_uae_or_fz_incorporated_label')} value={renderValue(tpData.juridicalPersonSpecifics.isUaeOrFzIncorporated, t)} />
              <DataDisplayRow label={t('juridical_person_incorporation_country_label')} value={renderValue(tpData.juridicalPersonSpecifics.incorporationCountry, t, 'country')} />
              <DataDisplayRow label={t('is_foreign_incorporated_uae_controlled_label')} value={renderValue(tpData.juridicalPersonSpecifics.isForeignIncorporatedUaeControlled, t)} />
              <DataDisplayRow label={t('jp_dta_resident_foreign_label')} value={renderValue(tpData.juridicalPersonSpecifics.isTaxResidentInForeignJurisdictionUnderDTA, t)} />
              <DataDisplayRow label={t('jp_dta_foreign_country_label')} value={renderValue(tpData.juridicalPersonSpecifics.dtaForeignJurisdictionCountry, t, 'country')} />
              <DataDisplayRow label={t('jp_dta_is_non_resident_label')} value={renderValue(tpData.juridicalPersonSpecifics.isNonResidentInUaeUnderDTA, t)} />
              <DataDisplayRow label={t('non_resident_juridical_filing_basis_label')} value={renderValue(tpData.juridicalPersonSpecifics.nonResidentFilingBasis, t, 'nonResidentFilingBasis')} />
              <DataDisplayRow label={t('is_bank_or_insurance_provider_label')} value={renderValue(tpData.juridicalPersonSpecifics.isBankOrInsuranceProvider, t)} />
              <DataDisplayRow label={t('is_mne_group_member_label')} value={renderValue(tpData.juridicalPersonSpecifics.isMneGroupMember, t)} />
              <DataDisplayRow label={t('revenue_for_tax_period_label')} value={renderValue(tpData.juridicalPersonSpecifics.revenueForTaxPeriod, t)} />
              <DataDisplayRow label={t('accounting_basis_label')} value={renderValue(tpData.juridicalPersonSpecifics.accountingBasis, t, 'accountingBasis')} />
              <DataDisplayRow label={t('conducts_multiple_businesses_label')} value={renderValue(tpData.juridicalPersonSpecifics.conductsMultipleBusinesses, t)} />
              <DataDisplayRow label={t('business_activities_count_label')} value={renderValue(tpData.juridicalPersonSpecifics.businessActivitiesCount, t)} />
          </dl>
          <ListDisplay label={t('business_activity_list_label')} items={tpData.juridicalPersonSpecifics.businessActivityList} renderItem={(item: BusinessActivityItem, i) => <DataDisplayRow label={`${t('activity_label')} ${i + 1}`} value={item.activity} />} />
          <ListDisplay label={t('revenue_percentage_by_sector_label')} items={tpData.juridicalPersonSpecifics.revenuePercentageBySector} renderItem={(item: RevenuePercentageItem, i) => <dl><DataDisplayRow label={`${t('sector_label')} ${i + 1}`} value={`${item.sector}: ${item.percentage}%`} /></dl>} />
          <dl>
              <DataDisplayRow label={t('is_partner_label')} value={renderValue(tpData.juridicalPersonSpecifics.isPartnerInUnincorporatedPartnerships, t)} />
          </dl>
          <ListDisplay label={t('unincorporated_partnership_details_label')} items={tpData.juridicalPersonSpecifics.unincorporatedPartnerships} renderItem={(item: UnincorporatedPartnershipItem) => <dl><DataDisplayRow label={item.name} value={`TRN: ${item.trn}`} /></dl>} />
          <dl>
              <DataDisplayRow label={t('is_govt_entity_label')} value={renderValue(tpData.juridicalPersonSpecifics.isGovernmentEntityOrAffiliate, t)} />
              <DataDisplayRow label={t('govt_entity_taxable_activity_label')} value={renderValue(tpData.juridicalPersonSpecifics.conductsTaxableActivityIfGovt, t)} />
              <DataDisplayRow label={t('is_fz_person_confirm_label')} value={renderValue(tpData.juridicalPersonSpecifics.isFreeZonePerson, t)} />
              <DataDisplayRow label={t('fz_election_forego_0_rate_label')} value={renderValue(tpData.juridicalPersonSpecifics.electionToForego0RateFZ, t)} />
              <DataDisplayRow label={t('fz_meets_requirements_label')} value={renderValue(tpData.juridicalPersonSpecifics.meetsQualifyingFZRequirements, t)} />
          </dl>
        </SectionWrapper>
        
        <SectionWrapper title={t('step2_title')} condition={hasData(elections)}>
          <dl>
              <DataDisplayRow label={t('realisation_basis_label')} value={renderValue(elections.realisationBasis, t)} />
              <DataDisplayRow label={t('realisation_basis_bank_option_label')} value={renderValue(elections.realisationBasisBankOption, t)} />
              <DataDisplayRow label={t('transitional_rules_immovable_property_label')} value={renderValue(elections.transitionalRulesImmovableProperty, t)} />
              <DataDisplayRow label={t('transitional_rules_intangible_assets_label')} value={renderValue(elections.transitionalRulesIntangibleAssets, t)} />
              <DataDisplayRow label={t('transitional_rules_financial_assets_liabilities_label')} value={renderValue(elections.transitionalRulesFinancialAssetsLiabilities, t)} />
              <DataDisplayRow label={t('sbr_election_label')} value={renderValue(elections.smallBusinessRelief, t)} />
              <DataDisplayRow label={t('election_qg_transfer_occurred_label')} value={renderValue(elections.hasTransfersWithinQGInPeriod, t)} />
              <DataDisplayRow label={t('transfers_within_qualifying_group_label')} value={renderValue(elections.transfersWithinQualifyingGroup, t)} />
              <DataDisplayRow label={t('election_brr_transfer_occurred_label')} value={renderValue(elections.hasBusinessRestructuringInPeriod, t)} />
              <DataDisplayRow label={t('business_restructuring_relief_label')} value={renderValue(elections.businessRestructuringRelief, t)} />
              <DataDisplayRow label={t('election_has_foreign_pes_label')} value={renderValue(elections.hasForeignPEs, t)} />
              <DataDisplayRow label={t('fpe_exemption_label')} value={renderValue(elections.foreignPermanentEstablishmentExemption, t)} />
          </dl>
        </SectionWrapper>

        {!elections.smallBusinessRelief && (
            <>
              <SectionWrapper title={t('step3_title')} condition={hasData(accounting)}>
                  <dl>
                      <DataDisplayRow label={t('accounting_revenue_label')} value={renderValue(accounting.incomeStatement_revenue, t)} />
                      <DataDisplayRow label={t('accounting_cost_of_sales_label')} value={renderValue(accounting.incomeStatement_costOfSales, t)} />
                      <DataDisplayRow label={t('accounting_gross_profit_label')} value={renderValue(accounting.incomeStatement_grossProfit, t)} />
                      <DataDisplayRow label={t('accounting_operating_expenses_label')} value={renderValue(accounting.incomeStatement_operatingExpenses, t)} />
                      <DataDisplayRow label={t('accounting_other_income_label')} value={renderValue(accounting.incomeStatement_otherIncome, t)} />
                      <DataDisplayRow label={t('accounting_income_label')} value={renderValue(accounting.accountingIncome, t)} isCalculated/>
                      <DataDisplayRow label={t('accounting_oci_items_net_tax_label')} value={renderValue(accounting.oci_itemsNetOfTax, t)} />
                      <DataDisplayRow label={t('accounting_total_assets_label')} value={renderValue(accounting.balanceSheet_totalAssets, t)} />
                      <DataDisplayRow label={t('accounting_total_liabilities_label')} value={renderValue(accounting.balanceSheet_totalLiabilities, t)} />
                      <DataDisplayRow label={t('accounting_total_equity_label')} value={renderValue(accounting.balanceSheet_totalEquity, t)} />
                      <DataDisplayRow label={t('financial_statements_audited_label')} value={renderValue(accounting.financialStatementsAudited, t)} />
                      <DataDisplayRow label={t('audit_opinion_label')} value={renderValue(accounting.auditOpinion, t)} />
                      <DataDisplayRow label={t('auditor_name_label')} value={renderValue(accounting.auditorName, t)} />
                  </dl>
              </SectionWrapper>
              
              <SectionWrapper title={t('step4_title')} condition={hasData(accountingAdjustments)}>
                  <dl>
                      <DataDisplayRow label={t('equityMethodInvestmentAccounting_label')} value={renderValue(accountingAdjustments.equityMethodInvestmentAccounting, t)} />
                      <DataDisplayRow label={t('equityMethodShareOfProfits_label')} value={renderValue(accountingAdjustments.equityMethodShareOfProfits, t)} />
                      <DataDisplayRow label={t('equityMethodShareOfLosses_label')} value={renderValue(accountingAdjustments.equityMethodShareOfLosses, t)} />
                      <DataDisplayRow label={t('unincorporatedPartnershipSeparateTP_label')} value={renderValue(accountingAdjustments.unincorporatedPartnershipSeparateTP, t)} />
                      <DataDisplayRow label={t('unincorporatedPartnershipNetProfits_label')} value={renderValue(accountingAdjustments.unincorporatedPartnershipNetProfits, t)} />
                      <DataDisplayRow label={t('unincorporatedPartnershipNetLosses_label')} value={renderValue(accountingAdjustments.unincorporatedPartnershipNetLosses, t)} />
                      <DataDisplayRow label={t('incomeLossesNotInISRecognised_label')} value={renderValue(accountingAdjustments.incomeLossesNotInISRecognised, t)} />
                      <DataDisplayRow label={t('gainsNotInIS_label')} value={renderValue(accountingAdjustments.gainsNotInIS, t)} />
                      <DataDisplayRow label={t('lossesNotInIS_label')} value={renderValue(accountingAdjustments.lossesNotInIS, t)} />
                      <DataDisplayRow label={t('unrealisedGainsRecognisedCurrentTaxPeriod_label')} value={renderValue(accountingAdjustments.unrealisedGainsRecognisedCurrentTaxPeriod, t)} />
                      <DataDisplayRow label={t('unrealisedLossesRecognisedCurrentTaxPeriod_label')} value={renderValue(accountingAdjustments.unrealisedLossesRecognisedCurrentTaxPeriod, t)} />
                      <DataDisplayRow label={t('deferredGainsRealisedCurrentTaxPeriod_label')} value={renderValue(accountingAdjustments.deferredGainsRealisedCurrentTaxPeriod, t)} />
                      <DataDisplayRow label={t('deferredLossesRealisedCurrentTaxPeriod_label')} value={renderValue(accountingAdjustments.deferredLossesRealisedCurrentTaxPeriod, t)} />
                      <DataDisplayRow label={t('gainsOnElectedImmovableProperty_label')} value={renderValue(accountingAdjustments.gainsOnElectedImmovableProperty, t)} />
                      <DataDisplayRow label={t('transitionalRulesIntangibleAssetsAdjustment_label')} value={renderValue(accountingAdjustments.transitionalRulesIntangibleAssetsAdjustment, t)} />
                      <DataDisplayRow label={t('transitionalRulesFinancialAssetsLiabilitiesAdjustment_label')} value={renderValue(accountingAdjustments.transitionalRulesFinancialAssetsLiabilitiesAdjustment, t)} />
                      <DataDisplayRow label={t('dividendsFromUaeResident_label')} value={renderValue(accountingAdjustments.dividendsFromUaeResident, t)} />
                      <DataDisplayRow label={t('totalDividendsFromUaeResident_label')} value={renderValue(accountingAdjustments.totalDividendsFromUaeResident, t)} />
                      <DataDisplayRow label={t('incomeLossesFromParticipatingInterest_label')} value={renderValue(accountingAdjustments.incomeLossesFromParticipatingInterest, t)} />
                      <DataDisplayRow label={t('incomeFromParticipatingInterestExempted_label')} value={renderValue(accountingAdjustments.incomeFromParticipatingInterestExempted, t)} />
                      <DataDisplayRow label={t('taxableIncomeLossFromEligibleForeignPE_label')} value={renderValue(accountingAdjustments.taxableIncomeLossFromEligibleForeignPE, t)} />
                  </dl>
              </SectionWrapper>

              <SectionWrapper title={t('step5_title')} condition={hasData(relief)}>
                  <dl>
                      <DataDisplayRow label={t('transfersWithinQualifyingGroupAdjustmentNeeded_label')} value={renderValue(relief.transfersWithinQualifyingGroupAdjustmentNeeded, t)} />
                      <DataDisplayRow label={t('gainsLossesSubjectToTgReliefCurrentPeriod_label')} value={renderValue(relief.gainsLossesSubjectToTgReliefCurrentPeriod, t)} />
                      <DataDisplayRow label={t('gainsLossesFromTgClawback_label')} value={renderValue(relief.gainsLossesFromTgClawback, t)} />
                      <DataDisplayRow label={t('businessRestructuringReliefAdjustmentNeeded_label')} value={renderValue(relief.businessRestructuringReliefAdjustmentNeeded, t)} />
                      <DataDisplayRow label={t('gainsLossesSubjectToBrrReliefCurrentPeriod_label')} value={renderValue(relief.gainsLossesSubjectToBrrReliefCurrentPeriod, t)} />
                      <DataDisplayRow label={t('gainsLossesFromBrrClawback_label')} value={renderValue(relief.gainsLossesFromBrrClawback, t)} />
                  </dl>
              </SectionWrapper>

              <SectionWrapper title={t('step6_title')} condition={hasData(otherAdjustments)}>
                  <dl>
                      <DataDisplayRow label={t('nonDeductibleEntertainmentExp_label')} value={renderValue(otherAdjustments.nonDeductibleEntertainmentExp, t)} />
                      <DataDisplayRow label={t('nonDeductiblePensionContributions_label')} value={renderValue(otherAdjustments.nonDeductiblePensionContributions, t)} />
                      <DataDisplayRow label={t('nonDeductibleCharitableDonationsNonQPBE_label')} value={renderValue(otherAdjustments.nonDeductibleCharitableDonationsNonQPBE, t)} />
                      <DataDisplayRow label={t('expenditureDerivingExemptIncomeNonInterest_label')} value={renderValue(otherAdjustments.expenditureDerivingExemptIncomeNonInterest, t)} />
                      <DataDisplayRow label={t('naturalPersonWithdrawals_label')} value={renderValue(otherAdjustments.naturalPersonWithdrawals, t)} />
                      <DataDisplayRow label={t('expensesNotWhollyExclusivelyBusiness_label')} value={renderValue(otherAdjustments.expensesNotWhollyExclusivelyBusiness, t)} />
                      <DataDisplayRow label={t('otherNonDeductibleExpenditure_label')} value={renderValue(otherAdjustments.otherNonDeductibleExpenditure, t)} />
                      <DataDisplayRow label={t('netInterestExpExceedsThreshold_label')} value={renderValue(otherAdjustments.netInterestExpExceedsThreshold, t)} />
                      <DataDisplayRow label={t('disallowedNetInterestExpCurrentPeriod_label')} value={renderValue(otherAdjustments.disallowedNetInterestExpCurrentPeriod, t)} />
                      <DataDisplayRow label={t('broughtForwardNieUtilised_label')} value={renderValue(otherAdjustments.broughtForwardNieUtilised, t)} />
                      <DataDisplayRow label={t('additionsArmslengthAdjustments_label')} value={renderValue(otherAdjustments.additionsArmslengthAdjustments, t)} />
                      <DataDisplayRow label={t('adjustmentsConnectedPersonsNonMarketValue_label')} value={renderValue(otherAdjustments.adjustmentsConnectedPersonsNonMarketValue, t)} />
                  </dl>
              </SectionWrapper>

              <SectionWrapper title={t('step7_title')}>
                <dl>
                  <DataDisplayRow label={t('s10_1_taxable_income_loss_display_label')} value={calculatedTaxableIncomeBeforeLoss.toFixed(2)} isCalculated />
                  <DataDisplayRow label={t('s10_2_losses_utilised_label')} value={renderValue(taxLiability.taxLossesUtilisedCurrentPeriod, t)} />
                  <DataDisplayRow label={t('s10_2_taxable_income_after_loss_label')} value={calculatedTaxableIncomeAfterLoss.toFixed(2)} isCalculated />
                  <DataDisplayRow label={t('s10_4_ct_liability_before_credits_label')} value={calculatedCtLiabilityBeforeCredits.toFixed(2)} isCalculated />
                  <DataDisplayRow label={t('s10_4_tax_credits_applied_label')} value={renderValue(taxLiability.taxCreditsApplied, t)} />
                  <DataDisplayRow label={t('s10_4_corporate_tax_payable_label')} value={calculatedPayable.toFixed(2)} isCalculated />
                </dl>
              </SectionWrapper>
            </>
        )}

        <SectionWrapper title={t('fz_schedule_main_title')} condition={isFzpSubjectTo0Rate && hasData(freeZoneSchedule)}>
            <dl>
              <DataDisplayRow label={t('fz_C1_deminimis_met_label')} value={renderValue(freeZoneSchedule.deMinimisMetResult, t)} isCalculated />
              <DataDisplayRow label={t('fz_qualifying_income_calculated_label')} value={freeZoneSchedule.calculatedQualifyingIncome} isCalculated />
              <DataDisplayRow label={t('fz_non_qualifying_income_calculated_label')} value={freeZoneSchedule.calculatedNonQualifyingIncome} isCalculated />
            </dl>
        </SectionWrapper>
        
        <SectionWrapper title={t('review_attachments_title')} condition={hasData(formData.schedule22_4_Attachments.attachments)}>
          <dl>
              {formData.schedule22_4_Attachments.attachments
                  .filter(att => !att.condition || att.condition(formData))
                  .map(att => (
                      <DataDisplayRow 
                          key={att.id}
                          label={t(att.documentKey as any) || att.documentName} 
                          value={att.file ? t('review_attachments_uploaded', { fileName: att.fileName || 'File' }) : (att.reasonIfNotProvided || t('review_attachments_not_provided', {reason: t('no_reason_provided_text_key')}))}
                      />
                  ))
              }
          </dl>
        </SectionWrapper>

        <SectionWrapper title={t('declaration_title')}>
          <dl>
            <DataDisplayRow label={t('signedBy_label')} value={reviewData.signedBy} />
            <DataDisplayRow label={t('capacity_label')} value={reviewData.capacity} />
            <DataDisplayRow label={t('submissionDate_label')} value={reviewData.submissionDate} />
            <DataDisplayRow label={t('declaration_confirmation_label')} value={renderValue(reviewData.declarationConfirmed, t)} />
          </dl>
        </SectionWrapper>
      </div>

      {/* Interactive part for UI only */}
      <div className="mt-8 pt-6 border-t border-gray-300 no-print">
        <h3 className="text-xl font-semibold text-[#1a2c46] mb-4">{t('declaration_title')}</h3>
        <Input
          label={t('signedBy_label')}
          type="text"
          id="signedBy"
          value={reviewData.signedBy}
          onChange={(e) => onChange('review', 'signedBy', e.target.value)}
          error={errors.signedBy as string}
          placeholder={t('signed_by_placeholder')}
        />
        <Input
          label={t('capacity_label')}
          type="text"
          id="capacity"
          value={reviewData.capacity}
          onChange={(e) => onChange('review', 'capacity', e.target.value)}
          error={errors.capacity as string}
          placeholder={t('capacity_placeholder')}
        />
        <Input
          label={t('submissionDate_label')}
          type="date"
          id="submissionDate"
          value={reviewData.submissionDate}
          onChange={(e) => onChange('review', 'submissionDate', e.target.value)}
          error={errors.submissionDate as string}
        />
        <Checkbox
          label={t('declaration_confirmation_label')}
          id="declarationConfirmed"
          checked={reviewData.declarationConfirmed}
          onChange={(e) => onChange('review', 'declarationConfirmed', e.target.checked)}
          error={errors.declarationConfirmed as string}
        />
        <p className="mt-4 text-sm text-gray-600 no-print">{t('step8_note')}</p>
      </div>
    </div>
  );
};